// MF-568 — Card 'pat-class-distribution' (design 1C): the shape of the class in three linked panels — a score dot
// strip over the bands, stanine bars 1-9 and band bars. Picking a band, a stanine or a dot highlights the same
// students in every panel and lists them below.
import { useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, Typography } from '@mui/material'
import { SUMMARY_PALETTE, INK, FONT, baseTextStyle, tooltipBase } from '../boardTheme'
import { resolveColors, useBoardStyle } from '../boardPalettes'
import { buttonProps, fmtDay, prepareClass, useChartResize } from './patShared'
import { GroupLine, Interpretation, Roster, StanineNote } from './PatParts'

const STANINES = [1, 2, 3, 4, 5, 6, 7, 8, 9]
const BAR_MAX = 84

// Count bars as buttons: the full bar is the group, the accent part of it the students currently selected.
const Bars = ({ items, onPick, primary, accent, title }) => {
  const max = Math.max(1, ...items.map((i) => i.total))
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: SUMMARY_PALETTE.ink, mb: 0.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: BAR_MAX + 36 }}>
        {items.map((i) => (
          <Box
            key={i.key}
            {...buttonProps(() => onPick(i), i.selected > 0 && i.selected === i.total)}
            aria-label={`${i.name}: ${i.total} ${i.total === 1 ? 'student' : 'students'}${i.selected ? `, ${i.selected} selected` : ''}`}
            sx={{
              flex: '1 1 0',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              cursor: 'pointer',
              height: '100%'
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: SUMMARY_PALETTE.ink }}>
              {i.total}
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: Math.max(2, (i.total / max) * BAR_MAX),
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '3px 3px 0 0',
                overflow: 'hidden',
                bgcolor: i.total ? primary : INK.axis,
                opacity: i.total ? 1 : 0.6
              }}
            >
              <Box sx={{ height: i.total ? `${(i.selected / i.total) * 100}%` : 0, bgcolor: accent }} />
            </Box>
            <Typography sx={{ fontSize: 10, color: SUMMARY_PALETTE.muted, mt: 0.25, whiteSpace: 'nowrap' }}>
              {i.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

const PatClassDistributionCard = ({ data }) => {
  const style = useBoardStyle()
  const [primary, accent] = resolveColors(style, {}, 2)
  const cls = useMemo(() => prepareClass(data?.rows || []), [data])
  const { students, bands } = cls
  const [pick, setPick] = useState({ ids: [], label: '' })
  const sel = useMemo(() => new Set(pick.ids), [pick])
  const { chartRef, boxRef } = useChartResize()
  const parsed = bands.filter((b) => b.lo !== null)

  const choose = (ids, label) =>
    setPick((cur) => (cur.label === label ? { ids: [], label: '' } : { ids, label }))
  const byBand = (key) => students.filter((s) => s.bandKey === key)
  const byStanine = (st) => students.filter((s) => s.stanine !== null && Math.round(s.stanine) === st)

  const option = useMemo(() => {
    const stack = new Map()
    const dots = students.map((s) => {
      const slot = Math.round(s.score / 2) // neighbouring scores would overlap, so they stack too
      const level = stack.get(slot) || 0
      stack.set(slot, level + 1)
      return { s, level }
    })
    const byId = new Map(students.map((s) => [s.id, s]))
    return {
      textStyle: baseTextStyle,
      animationDuration: 300,
      tooltip: {
        ...tooltipBase,
        trigger: 'item',
        formatter: (p) => {
          const s = p.value && byId.get(p.value[2])
          return s
            ? `<b>${s.name}</b><br/>Score ${s.score} · Band ${s.bandKey}<br/>${fmtDay(s.test_date)}`
            : ''
        }
      },
      grid: { top: 18, bottom: 30, left: 8, right: 12 },
      xAxis: {
        type: 'value',
        ...(parsed.length ? { min: parsed[0].lo, max: parsed[parsed.length - 1].hi + 1 } : { scale: true }),
        splitLine: { show: false },
        axisLine: { lineStyle: { color: INK.axis } },
        axisLabel: { color: INK.muted, fontSize: 10, hideOverlap: true },
        name: 'PAT scale score',
        nameLocation: 'middle',
        nameGap: 20,
        nameTextStyle: { color: INK.secondary, fontFamily: FONT, fontSize: 11 }
      },
      yAxis: { type: 'value', show: false, min: -0.5, max: Math.max(...dots.map((d) => d.level), 3) + 1 },
      series: [
        {
          type: 'scatter',
          symbolSize: 11,
          data: dots.map(({ s, level }) => ({
            value: [s.score, level, s.id],
            itemStyle: sel.has(s.id)
              ? { color: accent, borderColor: INK.primary, borderWidth: 2 }
              : { color: primary, opacity: sel.size ? 0.45 : 0.95 }
          })),
          markArea: {
            silent: false,
            label: {
              show: true,
              position: 'insideTop',
              color: INK.secondary,
              fontSize: 10,
              fontFamily: FONT
            },
            data: parsed.map((b) => [
              { name: b.key, xAxis: b.lo, itemStyle: { color: b.tint } },
              { xAxis: b.hi + 1 }
            ])
          }
        }
      ]
    }
  }, [students, parsed, sel, primary, accent])

  const onChartClick = (p) => {
    if (p?.componentType === 'markArea') {
      choose(
        byBand(p.name).map((s) => s.id),
        `Band ${p.name}`
      )
    } else if (p?.value) {
      const s = students.find((x) => x.id === p.value[2])
      if (s) choose([s.id], s.name)
    }
  }

  const stanineItems = STANINES.map((st) => {
    const list = byStanine(st)
    return {
      key: st,
      name: `Stanine ${st}`,
      label: String(st),
      total: list.length,
      selected: list.filter((s) => sel.has(s.id)).length,
      ids: list.map((s) => s.id)
    }
  })
  const bandItems = bands.map((b) => {
    const list = byBand(b.key)
    return {
      key: b.key,
      name: `Band ${b.key}`,
      label: b.key,
      total: list.length,
      selected: list.filter((s) => sel.has(s.id)).length,
      ids: list.map((s) => s.id)
    }
  })

  return (
    <Box sx={{ height: '100%', overflow: 'auto', minWidth: 0 }}>
      <GroupLine n={students.length} excluded={cls.excluded} noStanine={cls.noStanine} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: SUMMARY_PALETTE.ink, mt: 1 }}>
        Scores across the bands
      </Typography>
      <Box ref={boxRef} sx={{ minWidth: 0 }}>
        <ReactECharts
          ref={chartRef}
          option={option}
          notMerge
          style={{ height: 170, width: '100%' }}
          onEvents={{ click: onChartClick }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
          {cls.hasStanine ? (
            <Bars
              title='Stanine'
              items={stanineItems}
              primary={primary}
              accent={accent}
              onPick={(i) => choose(i.ids, i.name)}
            />
          ) : (
            <>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: SUMMARY_PALETTE.ink }}>
                Stanine
              </Typography>
              <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, mt: 0.5 }}>N/A</Typography>
            </>
          )}
        </Box>
        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
          <Bars
            title='Achievement band'
            items={bandItems}
            primary={primary}
            accent={accent}
            onPick={(i) => choose(i.ids, i.name)}
          />
        </Box>
      </Box>
      {cls.noStanine > 0 ? <StanineNote /> : null}
      <Roster
        students={students.filter((s) => sel.has(s.id))}
        label={pick.label}
        onClear={() => setPick({ ids: [], label: '' })}
      />
      <Interpretation />
    </Box>
  )
}

export default PatClassDistributionCard
